import React, { useEffect, useState } from "react";
import { getOptions, selectOption, startSurvey } from "../api/surveyApi";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import "./surveyStep.css";

export default function SurveyStep() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const parentId = searchParams.get("parentId");
  const stepNo = Number(searchParams.get("stepNo") || 1);

  const [options, setOptions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    startSurvey(slug).catch(() => {});
  }, [slug]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    getOptions(slug, parentId)
      .then((res) => {
        if (!mounted) return;
        setOptions(res.data.options || []);
        setSelectedId(null);
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || "";
        if (err?.response?.status === 401) navigate("/login");
        else alert(msg || "Failed to load options");
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [slug, parentId, navigate]);

  const handleSubmit = async () => {
    if (!selectedId) return alert("Please select one option");

    try {
      setSubmitting(true);
      const res = await selectOption(slug, Number(selectedId), stepNo);

      if (res.data.done) {
        navigate(`/survey/${slug}/done`);
        return;
      }

      const nextParentId = selectedId;
      const nextStepNo = stepNo + 1;

      if (nextStepNo >= 3) {
        navigate(
          `/survey/${slug}/accordion?parentId=${nextParentId}&stepNo=${nextStepNo}`
        );
      } else {
        navigate(`/survey/${slug}?parentId=${nextParentId}&stepNo=${nextStepNo}`);
      }
    } catch (err) {
      alert(err?.response?.data?.message || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="sv-stepPage">
        <div className="sv-stepShell">
          <div className="sv-loadingCard">
            <div className="sv-skeletonTitle" />
            <div className="sv-skeletonLine" />
            <div className="sv-skeletonLine" />
            <div className="sv-skeletonLine" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sv-stepPage">
      <div className="sv-stepShell">
        <header className="sv-stepHeader">
          <div className="sv-badge">Step {stepNo}</div>
          <h2 className="sv-title">Choose one option</h2>
          <p className="sv-subtitle">This helps us personalize the care plan.</p>
        </header>

        <div className="sv-grid">
          {options.map((opt) => {
            const active = selectedId === String(opt.id);
            return (
              <button
                type="button"
                key={opt.id}
                onClick={() => setSelectedId(String(opt.id))}
                className={`sv-optionCard ${active ? "is-active" : ""}`}
              >
                <span className={`sv-radio ${active ? "is-on" : ""}`} aria-hidden="true" />
                <span className="sv-optionText">{opt.label}</span>
              </button>
            );
          })}
        </div>

        <div className="sv-footer">
          <button
            className="sv-submitBtn"
            onClick={handleSubmit}
            disabled={!selectedId || submitting}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>

          <div className="sv-hint">
            {!selectedId ? "Select an option to continue" : "Ready to continue ✅"}
          </div>
        </div>
      </div>
    </div>
  );
}